import { SidebarInset, SidebarProvider } from "@repo/ui/components/sidebar"
import { type CSSProperties, Suspense } from "react"
import { Navigate, Outlet, useLocation } from "react-router"

import { AppSidebar } from "@/components/app-sidebar"
import { BreadcrumbProvider } from "@/components/breadcrumb-provider"
import { RouteErrorBoundary } from "@/components/route-error-boundary"
import { Topbar } from "@/components/topbar"
import { useSession } from "@/lib/auth-client"

function sidebarDefaultOpen() {
  const match = document.cookie.match(/(?:^|;\s*)sidebar_state=([^;]*)/)
  return match ? match[1] !== "false" : true
}

export function DashboardLayout() {
  const { data: session, isPending } = useSession()
  const { pathname } = useLocation()

  if (isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    )
  }

  if (!session) {
    return <Navigate to="/login" replace />
  }

  if (session.user.role !== "admin") {
    return (
      <div className="flex min-h-svh items-center justify-center p-6 text-sm">
        <p>This dashboard is admin-only. Signed in as {session.user.email}.</p>
      </div>
    )
  }

  return (
    <SidebarProvider
      defaultOpen={sidebarDefaultOpen()}
      style={
        {
          "--sidebar-width": "16rem",
          "--header-height": "4rem",
        } as CSSProperties
      }
    >
      <AppSidebar user={session.user} />
      <SidebarInset>
        <BreadcrumbProvider>
          <Topbar />
          <div className="flex-1 p-4 sm:p-6">
            <RouteErrorBoundary resetKey={pathname}>
              <Suspense
                fallback={
                  <p className="text-sm text-muted-foreground">Loading…</p>
                }
              >
                <Outlet />
              </Suspense>
            </RouteErrorBoundary>
          </div>
        </BreadcrumbProvider>
      </SidebarInset>
    </SidebarProvider>
  )
}
